import { supabase } from '../lib/supabaseClient';
import { Deck, DeckWithCards, CreateDeckRequest, UpdateDeckRequest } from '../types/decks';
import { CardSummary } from '../types/cards';

export async function fetchDecks(): Promise<Deck[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { data, error } = await supabase
    .from('decks')
    .select('*')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
}

export async function fetchDeckWithCards(deckId: string): Promise<DeckWithCards | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // 덱 정보 가져오기
  const { data: deck, error: deckError } = await supabase
    .from('decks')
    .select('*')
    .eq('id', deckId)
    .eq('user_id', session.user.id)
    .single();

  if (deckError || !deck) {
    return null;
  }

  // 카드 정보 가져오기
  if (deck.card_ids.length === 0) {
    return {
      ...deck,
      cards: [],
    };
  }

  const { data: cards, error: cardsError } = await supabase
    .from('cards')
    .select('id, name, image_url, grade, cost, attack, defense, hp, attribute')
    .in('id', deck.card_ids);

  if (cardsError) {
    throw cardsError;
  }

  // card_ids 순서대로 정렬
  type CardInfo = {
    id: number;
    name: string;
    image_url: string;
    grade: string;
    cost?: number;
    attack?: number;
    defense?: number;
    hp?: number;
    attribute?: string;
  };
  const cardMap = new Map<number, CardInfo>((cards || []).map((card) => [card.id, card]));
  const sortedCards: CardInfo[] = deck.card_ids
    .map((id: number) => cardMap.get(id))
    .filter((card: CardInfo | undefined): card is CardInfo => card !== undefined);

  return {
    ...deck,
    cards: sortedCards,
  };
}

export async function createDeck(deck: CreateDeckRequest): Promise<Deck> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { data, error } = await supabase
    .from('decks')
    .insert({
      user_id: session.user.id,
      name: deck.name,
      card_ids: deck.card_ids,
      is_active: deck.is_active || false,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export async function updateDeck(deckId: string, updates: UpdateDeckRequest): Promise<Deck> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { data, error } = await supabase
    .from('decks')
    .update(updates)
    .eq('id', deckId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export async function deleteDeck(deckId: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { error } = await supabase
    .from('decks')
    .delete()
    .eq('id', deckId)
    .eq('user_id', session.user.id);

  if (error) {
    throw error;
  }
}

export async function setActiveDeck(deckId: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // 먼저 모든 덱을 비활성화
  await supabase
    .from('decks')
    .update({ is_active: false })
    .eq('user_id', session.user.id);

  // 선택한 덱을 활성화
  const { error } = await supabase
    .from('decks')
    .update({ is_active: true })
    .eq('id', deckId)
    .eq('user_id', session.user.id);

  if (error) {
    throw error;
  }
}

// 덱 검증 함수
export function validateDeck(cardIds: number[], userCards: CardSummary[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // 최소/최대 카드 수 검증
  if (cardIds.length < 30) {
    errors.push(`덱은 최소 30장이 필요합니다. (현재: ${cardIds.length}장)`);
  }
  if (cardIds.length > 30) {
    errors.push(`덱은 최대 30장까지 가능합니다. (현재: ${cardIds.length}장)`);
  }

  // 보유 카드 검증
  const userCardMap = new Map(userCards.map((card) => [card.id, card.count]));
  const cardCountMap = new Map<number, number>();

  for (const cardId of cardIds) {
    const userCount = userCardMap.get(cardId) || 0;
    const currentCount = cardCountMap.get(cardId) || 0;

    if (userCount < currentCount + 1) {
      errors.push(`카드 #${cardId}를 보유하지 않았거나 부족합니다.`);
    }

    cardCountMap.set(cardId, currentCount + 1);
  }

  // 중복 제한 검증
  for (const [cardId, count] of Array.from(cardCountMap.entries())) {
    if (count > 3) {
      errors.push(`카드 #${cardId}는 최대 3장까지 덱에 포함할 수 있습니다.`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

